import { useMemo, useState } from 'react';
import {
  Box, Typography, Card, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Tooltip, ButtonBase,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import dayjs from 'dayjs';
import api from '../../services/api';

// Aba Acompanhamento do projeto (v1.19.0): mostra o último registro em leitura, lista os anteriores
// e abre o formulário (novo ou correção) numa janela. O novo já vem preenchido com o anterior.

const SITUACOES = {
  verde:    { label: 'No ritmo', cor: '#639922', fundo: '#EAF3DE' },
  amarelo:  { label: 'Atenção',  cor: '#BA7517', fundo: '#FAEEDA' },
  vermelho: { label: 'Crítico',  cor: '#E24B4A', fundo: '#FCEBEB' },
};
const SEM_SITUACAO = { label: 'Sem situação', cor: '#94a3b8', fundo: '#f1f5f9' };

const GRUPOS = [
  {
    titulo: 'Atividades', cor: '#2563EB',
    campos: [
      { chave: 'atividades_planejadas', rotulo: 'Atividades planejadas' },
      { chave: 'atividades_executadas', rotulo: 'Atividades executadas' },
      { chave: 'atividades_equipe', rotulo: 'Atividades a realizar pela equipe' },
    ],
  },
  {
    titulo: 'Entregas', cor: '#16A34A',
    campos: [
      { chave: 'entregas_previstas', rotulo: 'Entregas previstas' },
      { chave: 'entregas_realizadas', rotulo: 'Entregas realizadas' },
    ],
  },
  {
    titulo: 'Riscos e apoio', cor: '#D97706',
    campos: [
      { chave: 'pontos_criticos', rotulo: 'Pontos críticos' },
      { chave: 'oportunidades', rotulo: 'Oportunidades' },
      { chave: 'cadeia_de_ajuda', rotulo: 'Cadeia de ajuda' },
    ],
  },
];
const CHAVES_TEXTO = [...GRUPOS.flatMap((g) => g.campos.map((c) => c.chave)), 'observacoes'];

const situacaoDe = (a) => SITUACOES[a.situacao] || SEM_SITUACAO;

function textoDoPeriodo(a) {
  if (a.periodo_inicio && a.periodo_fim) {
    const i = dayjs(a.periodo_inicio);
    const f = dayjs(a.periodo_fim);
    return i.year() === f.year()
      ? `${i.format('DD/MM')} a ${f.format('DD/MM/YYYY')}`
      : `${i.format('DD/MM/YYYY')} a ${f.format('DD/MM/YYYY')}`;
  }
  if (a.periodo_inicio) return `A partir de ${dayjs(a.periodo_inicio).format('DD/MM/YYYY')}`;
  if (a.periodo_fim) return `Até ${dayjs(a.periodo_fim).format('DD/MM/YYYY')}`;
  return 'Período não informado';
}

const textoDoRegistro = (a) => `${a.criado_por_nome || 'Autor desconhecido'} · ${dayjs(a.criado_em).format('DD/MM/YYYY [às] HH:mm')}`;

function resumo(a) {
  const campo = CHAVES_TEXTO.find((c) => (a[c] || '').trim());
  if (!campo) return 'Sem texto registrado';
  const t = a[campo].trim().replace(/\s+/g, ' ');
  return t.length > 110 ? `${t.slice(0, 110)}…` : t;
}

// Data de referência para ordenar: fim do período, ou início, ou o dia em que foi registrado.
const chaveDeOrdem = (a) => `${a.periodo_fim || a.periodo_inicio || String(a.criado_em).slice(0, 10)}|${a.criado_em}`;

function CartaoGrupo({ grupo, a }) {
  return (
    <Card sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
      <Box sx={{ backgroundColor: grupo.cor, px: 2.5, py: 1.1 }}>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14 }}>{grupo.titulo}</Typography>
      </Box>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {grupo.campos.map((c) => {
          const valor = (a[c.chave] || '').trim();
          return (
            <Box key={c.chave}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', mb: 0.4 }}>{c.rotulo}</Typography>
              <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', color: valor ? '#1a1a2e' : '#b6bfcc' }}>
                {valor || 'Nada registrado'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
}

function ConteudoDoRegistro({ a }) {
  const obs = (a.observacoes || '').trim();
  return (
    <Box>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        {GRUPOS.map((g) => <CartaoGrupo key={g.titulo} grupo={g} a={a} />)}
      </Box>
      <Card sx={{ borderRadius: 3, mt: 2, p: 2.5 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', mb: 0.4 }}>Observações</Typography>
        <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', color: obs ? '#1a1a2e' : '#b6bfcc' }}>
          {obs || 'Nada registrado'}
        </Typography>
      </Card>
    </Box>
  );
}

function SeloSituacao({ a, grande = false }) {
  const s = situacaoDe(a);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, px: grande ? 1.4 : 1, py: grande ? 0.6 : 0.3, borderRadius: 5, backgroundColor: s.fundo, color: s.cor }}>
      <Box sx={{ width: grande ? 10 : 8, height: grande ? 10 : 8, borderRadius: '50%', backgroundColor: s.cor }} />
      <Typography sx={{ fontWeight: 700, fontSize: grande ? 14 : 12, whiteSpace: 'nowrap' }}>{s.label}</Typography>
    </Box>
  );
}

const semanaAtual = () => {
  const inicio = dayjs().startOf('week').add(1, 'day');
  return { periodo_inicio: inicio.format('YYYY-MM-DD'), periodo_fim: inicio.add(6, 'day').format('YYYY-MM-DD') };
};

export default function AcompanhamentoProjeto({ projetoId, acompanhamentos, aoAtualizar }) {
  const lista = useMemo(
    () => [...(acompanhamentos || [])].sort((a, b) => chaveDeOrdem(b).localeCompare(chaveDeOrdem(a))),
    [acompanhamentos],
  );
  const ultimo = lista[0] || null;
  const anteriores = lista.slice(1);

  const [form, setForm] = useState(null);          // formulário aberto (novo ou correção)
  const [editando, setEditando] = useState(null);  // registro em correção (null = novo)
  const [copiadoDe, setCopiadoDe] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');
  const [verId, setVerId] = useState(null);        // registro anterior aberto para leitura
  const [aviso, setAviso] = useState(null);        // { tipo, texto } na página

  const registroAberto = lista.find((a) => a.id === verId) || null;

  const mensagemDoErro = (err, padrao) => {
    const dados = err?.response?.data;
    const primeiro = dados && typeof dados === 'object' ? Object.values(dados)[0] : null;
    return dados?.detail || (Array.isArray(primeiro) ? primeiro[0] : primeiro) || padrao;
  };

  const abrirNovo = () => {
    setErroForm('');
    setEditando(null);
    const base = { ...semanaAtual(), situacao: ultimo?.situacao || '' };
    CHAVES_TEXTO.forEach((c) => { base[c] = ultimo ? (ultimo[c] || '') : ''; });
    setForm(base);
    setCopiadoDe(ultimo);
  };

  const abrirCorrecao = (a) => {
    setErroForm('');
    setVerId(null);
    setEditando(a);
    setCopiadoDe(null);
    const base = { periodo_inicio: a.periodo_inicio || '', periodo_fim: a.periodo_fim || '', situacao: a.situacao || '' };
    CHAVES_TEXTO.forEach((c) => { base[c] = a[c] || ''; });
    setForm(base);
  };

  const fecharForm = () => { if (!salvando) setForm(null); };

  const salvar = async () => {
    setErroForm('');
    const temTexto = CHAVES_TEXTO.some((c) => (form[c] || '').trim());
    if (!temTexto && !form.situacao) {
      setErroForm('Preencha ao menos um campo ou escolha a situação do projeto.');
      return;
    }
    if (form.periodo_inicio && form.periodo_fim && form.periodo_fim < form.periodo_inicio) {
      setErroForm('O fim do período não pode ser antes do início.');
      return;
    }
    const dados = {
      ...Object.fromEntries(CHAVES_TEXTO.map((c) => [c, form[c] || ''])),
      periodo_inicio: form.periodo_inicio || null,
      periodo_fim: form.periodo_fim || null,
      situacao: form.situacao || '',
    };
    setSalvando(true);
    try {
      if (editando) {
        await api.patch(`/acompanhamentos/${editando.id}/`, dados);
        setAviso({ tipo: 'success', texto: 'Acompanhamento corrigido com sucesso!' });
      } else {
        await api.post('/acompanhamentos/', { ...dados, projeto: projetoId });
        setAviso({ tipo: 'success', texto: 'Acompanhamento registrado com sucesso!' });
      }
      setForm(null);
      aoAtualizar();
    } catch (err) {
      setErroForm(mensagemDoErro(err, 'Não foi possível salvar o acompanhamento.'));
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir o acompanhamento de ${textoDoPeriodo(a)}? Essa ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/acompanhamentos/${a.id}/`);
      setVerId(null);
      setAviso({ tipo: 'success', texto: 'Acompanhamento excluído.' });
      aoAtualizar();
    } catch (err) {
      setAviso({ tipo: 'error', texto: mensagemDoErro(err, 'Não foi possível excluir o acompanhamento.') });
    }
  };

  const mudar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const Acoes = ({ a }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="Corrigir este acompanhamento">
        <IconButton aria-label="Corrigir acompanhamento" size="small" onClick={() => abrirCorrecao(a)}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
      </Tooltip>
      <Tooltip title="Excluir este acompanhamento">
        <IconButton aria-label="Excluir acompanhamento" size="small" onClick={() => excluir(a)}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      {aviso && <Alert severity={aviso.tipo} sx={{ mb: 2 }} onClose={() => setAviso(null)}>{aviso.texto}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>Acompanhamento do projeto</Typography>
          <Typography variant="body2" color="text.secondary">
            {lista.length === 0
              ? 'Nenhum registro ainda.'
              : `${lista.length} ${lista.length === 1 ? 'registro' : 'registros'}. O mais recente aparece primeiro.`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo} sx={{ backgroundColor: '#13375F' }}>
          Novo acompanhamento
        </Button>
      </Box>

      {!ultimo ? (
        <Card sx={{ borderRadius: 3, p: 5, textAlign: 'center' }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.5, backgroundColor: '#eef0f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AssignmentIcon sx={{ color: '#94a3b8' }} />
          </Box>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Este projeto ainda não tem acompanhamento</Typography>
          <Typography variant="body2" color="text.secondary">
            Use o botão “Novo acompanhamento” para registrar o que foi feito, as entregas e os pontos de atenção do período.
          </Typography>
        </Card>
      ) : (
        <>
          <Card sx={{ borderRadius: 3, mb: 2, borderLeft: `6px solid ${situacaoDe(ultimo).cor}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, p: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 0.5 }}>Último acompanhamento</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e' }}>{textoDoPeriodo(ultimo)}</Typography>
                  <SeloSituacao a={ultimo} grande />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{textoDoRegistro(ultimo)}</Typography>
              </Box>
              <Acoes a={ultimo} />
            </Box>
          </Card>

          <ConteudoDoRegistro a={ultimo} />

          {anteriores.length > 0 && (
            <Card sx={{ borderRadius: 3, mt: 3, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #eef0f3' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Registros anteriores ({anteriores.length})</Typography>
              </Box>
              {anteriores.map((a, i) => (
                <ButtonBase
                  key={a.id} onClick={() => setVerId(a.id)}
                  sx={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left',
                    px: 2.5, py: 1.5, borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    '&.Mui-focusVisible': { outline: '2px solid #2563EB', outlineOffset: -2 },
                  }}
                >
                  <Box sx={{ width: { xs: 130, sm: 200 }, flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{textoDoPeriodo(a)}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>{a.criado_por_nome || 'Autor desconhecido'}</Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0, width: 118 }}><SeloSituacao a={a} /></Box>
                  <Typography sx={{ flex: 1, fontSize: 13.5, color: '#475569', minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
                    {resumo(a)}
                  </Typography>
                  <ChevronRightIcon sx={{ color: '#94a3b8', ml: 'auto' }} />
                </ButtonBase>
              ))}
            </Card>
          )}
        </>
      )}

      {/* Registro anterior em leitura */}
      <Dialog open={Boolean(registroAberto)} onClose={() => setVerId(null)} maxWidth="lg" fullWidth>
        {registroAberto && (
          <>
            <DialogTitle sx={{ pr: 7 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {textoDoPeriodo(registroAberto)} <SeloSituacao a={registroAberto} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>{textoDoRegistro(registroAberto)}</Typography>
              <IconButton aria-label="Fechar" onClick={() => setVerId(null)} sx={{ position: 'absolute', right: 12, top: 12 }}>
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent><Box sx={{ pt: 1 }}><ConteudoDoRegistro a={registroAberto} /></Box></DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
              <Acoes a={registroAberto} />
              <Button onClick={() => setVerId(null)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Formulário: novo ou correção */}
      <Dialog open={Boolean(form)} onClose={fecharForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 7 }}>
          {editando ? 'Corrigir acompanhamento' : 'Novo acompanhamento'}
          <IconButton aria-label="Fechar" onClick={fecharForm} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        {form && (
          <DialogContent>
            {erroForm && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroForm('')}>{erroForm}</Alert>}
            {copiadoDe && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Os campos vieram preenchidos com o registro de {textoDoPeriodo(copiadoDe)}. Revise e ajuste o que mudou antes de salvar.
              </Alert>
            )}

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mt: 0.5, mb: 2 }}>
              <TextField label="Início do período" type="date" value={form.periodo_inicio}
                onChange={(e) => mudar('periodo_inicio', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Fim do período" type="date" value={form.periodo_fim}
                onChange={(e) => mudar('periodo_fim', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
            </Box>

            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569', mb: 1 }}>Como está o projeto?</Typography>
            <Box role="radiogroup" aria-label="Situação do projeto" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {Object.entries(SITUACOES).map(([chave, s]) => {
                const ativo = form.situacao === chave;
                return (
                  <ButtonBase
                    key={chave} role="radio" aria-checked={ativo}
                    onClick={() => mudar('situacao', ativo ? '' : chave)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 2, height: 40, borderRadius: 2,
                      border: `2px solid ${ativo ? s.cor : '#e2e8f0'}`, backgroundColor: ativo ? s.fundo : 'white',
                      color: ativo ? s.cor : '#475569', fontWeight: 700, fontSize: 14,
                      '&.Mui-focusVisible': { outline: '2px solid #2563EB', outlineOffset: 2 },
                    }}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.cor }} />
                    {s.label}
                  </ButtonBase>
                );
              })}
            </Box>

            {GRUPOS.map((g) => (
              <Box key={g.titulo} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 16, borderRadius: 2, backgroundColor: g.cor }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>{g.titulo}</Typography>
                </Box>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  {g.campos.map((c) => (
                    <TextField key={c.chave} label={c.rotulo} multiline minRows={3} fullWidth
                      value={form[c.chave]} onChange={(e) => mudar(c.chave, e.target.value)} />
                  ))}
                </Box>
              </Box>
            ))}

            <TextField label="Observações" multiline minRows={3} fullWidth
              value={form.observacoes} onChange={(e) => mudar('observacoes', e.target.value)} />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={fecharForm} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando} sx={{ backgroundColor: '#13375F' }}>
            {salvando ? 'Salvando...' : editando ? 'Salvar correção' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
